// -- [Stats / proof band - blue background; dynamic from API or default fallback]

import { useState } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import type { RoleKey } from '@/types/content'
import type {
  CurrentData,
  FileAccessTimeSavedItem,
  IncomingEmailFilingItem,
  BulkUploadFilingItem,
  LuceneSearchStatsItem,
} from '@/lib/currentDataApi'
import { useCurrentData, getFallbackDisplay } from '@/hooks/useCurrentData'
import {
  formatSecondsToDuration,
  formatSecondsToHoursMinutesSeconds,
  formatAverageSearchTime,
} from '@/lib/formatDuration'
import { AnimatedStatValue } from './AnimatedStatValue'

function formatPages(n: number): string {
  return n.toLocaleString('en-AU', { maximumFractionDigits: 0 })
}

function formatCurrencyWhole(n: number): string {
  return n.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'AUD',
  })
}

interface StatsBandProps {
  roleKey: RoleKey
}

// -- [Breakdown callout: Document Retrieval, Filing Time, Search]

export function StatsBand({ roleKey }: StatsBandProps) {
  const { display, data } = useCurrentData(roleKey)
  const stats = display ?? getFallbackDisplay(roleKey)
  const rawData: CurrentData | null = data

  const [breakdownOpen, setBreakdownOpen] = useState(false)

  const totalPagesFormatted = formatPages(stats.estimatedTotalPages)
  const timeSavedFormatted = formatSecondsToHoursMinutesSeconds(stats.estimatedTotalTimeSavedSeconds)
  const transactionsFormatted = formatCurrencyWhole(Math.round(stats.estimatedTransactionsAud))
  const dealershipsFormatted = stats.activeDealerships.toLocaleString('en-AU')

  const lucene = rawData?.luceneSearchStatsLast3Months
  const searchTimeFormatted =
    lucene && lucene.count > 0
      ? formatAverageSearchTime(lucene.processingTimeTotal, lucene.count)
      : '—'

  const fileAccess = rawData?.fileAccessTimeSaved
  const incomingEmail = rawData?.incomingEmailFiling
  const bulkUpload = rawData?.bulkUploadFiling
  const hasBreakdown = fileAccess || incomingEmail || bulkUpload

  return (
    <section id="stats" className="py-16 px-6 bg-brand-blue text-white">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-center text-xl min-[480px]:text-2xl font-semibold text-white/95 mb-10">
          <strong>Drivible</strong> at a glance
        </h2>
        <Collapsible.Root open={breakdownOpen} onOpenChange={setBreakdownOpen}>
          {/* -- Time saved on its own row, full width and larger */}
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className={`group w-full text-center py-6 rounded-lg transition-colors outline-none focus:ring-2 focus:ring-white/50 ${
                hasBreakdown ? 'hover:bg-white/10' : 'cursor-default'
              }`}
              disabled={!hasBreakdown}
            >
              <div className="text-4xl min-[480px]:text-5xl min-[1000px]:text-6xl font-bold mb-2 flex items-center justify-center overflow-visible w-full max-w-full">
                <AnimatedStatValue formatted={timeSavedFormatted} wrap />
              </div>
              <div className="text-white text-2xl min-[480px]:text-3xl md:text-4xl">
                Estimated <strong className="font-bold">time saved</strong>
                {hasBreakdown && (
                  <span className="block text-white/90 text-sm mt-2 font-medium">
                    {breakdownOpen ? 'Hide breakdown' : 'Tap for breakdown'}
                  </span>
                )}
              </div>
            </button>
          </Collapsible.Trigger>

          {/* -- Other four stats in a row below */}
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 min-[1000px]:grid-cols-4 gap-8 mt-10">
            <div className="text-center min-h-[4.5rem] flex flex-col items-center justify-center">
              <div className="text-3xl min-[1000px]:text-4xl font-bold mb-2 h-[1.2em] flex items-center justify-center overflow-hidden w-full">
                <AnimatedStatValue formatted={totalPagesFormatted} />
              </div>
              <div className="text-white/90 text-sm">Total pages stored</div>
            </div>
            <div className="text-center min-h-[4.5rem] flex flex-col items-center justify-center">
              <div className="text-3xl min-[1000px]:text-4xl font-bold mb-2">
                {searchTimeFormatted}
              </div>
              <div className="text-white/90 text-sm">Avg search time</div>
            </div>
            <div className="text-center min-h-[4.5rem] flex flex-col items-center justify-center">
              <div className="text-3xl min-[1000px]:text-4xl font-bold mb-2 h-[1.2em] flex items-center justify-center overflow-hidden w-full">
                <AnimatedStatValue formatted={dealershipsFormatted} />
              </div>
              <div className="text-white/90 text-sm">Dealerships</div>
            </div>
            <div className="text-center min-h-[4.5rem] flex flex-col items-center justify-center">
              <div className="text-3xl min-[1000px]:text-4xl font-bold mb-2 h-[1.2em] flex items-center justify-center overflow-hidden w-full">
                <AnimatedStatValue formatted={transactionsFormatted} />
              </div>
              <div className="text-white/90 text-sm">Transactions (AUD)</div>
            </div>
          </div>
          <Collapsible.Content>
            <TimeSavedBreakdown
              totalEstimatedTimeSavedSeconds={stats.estimatedTotalTimeSavedSeconds}
              fileAccess={fileAccess}
              incomingEmail={incomingEmail}
              bulkUpload={bulkUpload}
              luceneSearchStats={rawData?.luceneSearchStats ?? null}
            />
          </Collapsible.Content>
        </Collapsible.Root>
      </div>
    </section>
  )
}

// -- [Breakdown callout: Document Retrieval, Filing Time, Search]

// -- [Compact duration for breakdown callout; seconds are correct; API often sends whole-minute values]
function formatSecondsToCompactDuration(s: number): string {
  const total = Math.max(0, Math.floor(s))
  const days = Math.floor(total / 86400)
  const remainderAfterDays = total % 86400
  const hours = Math.floor(remainderAfterDays / 3600)
  const remainderAfterHours = remainderAfterDays % 3600
  const min = Math.floor(remainderAfterHours / 60)
  const sec = remainderAfterHours % 60
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (min > 0) parts.push(`${min} min`)
  parts.push(`${sec}s`)
  return parts.join(' ')
}

// -- [Emphasised duration for breakdown line items; compact format]
function Duration({ seconds }: { seconds: number }) {
  return (
    <span className="font-semibold text-white/95">
      {formatSecondsToCompactDuration(seconds)}
    </span>
  )
}

interface TimeSavedBreakdownProps {
  totalEstimatedTimeSavedSeconds: number
  fileAccess?: FileAccessTimeSavedItem | null
  incomingEmail?: IncomingEmailFilingItem | null
  bulkUpload?: BulkUploadFilingItem | null
  luceneSearchStats?: LuceneSearchStatsItem | null
}

function TimeSavedBreakdown({
  totalEstimatedTimeSavedSeconds,
  fileAccess,
  incomingEmail,
  bulkUpload,
  luceneSearchStats,
}: TimeSavedBreakdownProps) {
  return (
    <div className="mt-8 p-6 rounded-xl bg-white/10 text-left max-w-3xl mx-auto">
      <h2 className="text-2xl text-white font-semibold mb-2">
        How the <strong className="font-bold">Estimated Time Saved</strong> is calculated
      </h2>
      <p className="text-white/85 text-sm mb-6">
        These are rough estimates and the actual time saved may vary depending on the document, filing method and the user's environment.
      </p>

      <section className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">Total Estimated Time Saved</h3>
        <p className="text-sm text-white/85 mb-1">
          <span className="font-semibold text-white/95">
            {formatSecondsToDuration(totalEstimatedTimeSavedSeconds)}
          </span>
        </p>
      </section>

      {fileAccess && (
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-2">Document Retrieval</h3>
          <p className="text-sm text-white/85 mb-1">
            <Duration seconds={fileAccess.estimatedTotalTimeSavedSeconds} /> total.
          </p>
          <p className="text-sm text-white/85 mt-2">
            We use a tiered system based on how long ago a document was uploaded to Drivible compared with each time a file was accessed.<br />
            For example, if a file was uploaded within the last 4 hours when someone accessed it, we assume it would normally have been on their desk and only count 10s for savings.<br />
            But if it has been over 12 hours since it was uploaded we assume it would have been in a box nearby and would have taken a minute to find.<br />
            Anything beyond 2 days we assume it would have been in a box in the filing room and would have taken at least 10 minutes to find.<br />
            We also remove the total time waiting for our search results to load.<br />
          </p>
        </section>
      )}

      {(incomingEmail || bulkUpload) && (
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-2">Sorting and Filing time</h3>
          {incomingEmail && bulkUpload && (<p className="text-sm text-white/85 mb-2"><Duration seconds={(incomingEmail.estimatedTotalTimeSavedSeconds ?? 0) + (bulkUpload.estimatedTotalTimeSavedSeconds ?? 0)} /> total. </p>)}
          {incomingEmail && (
            <p className="text-sm text-white/85 mb-1">
              Emails: <Duration seconds={incomingEmail.estimatedFilingTimeSeconds} /> sorting and filing −{' '}
              <Duration seconds={incomingEmail.estimatedScanTimeSeconds} /> scanning ={' '}
              <Duration seconds={incomingEmail.estimatedTotalTimeSavedSeconds} /> saved.
            </p>
          )}
          {bulkUpload && (
            <p className="text-sm text-white/85 mb-1">
              Bulk Documents: <Duration seconds={bulkUpload.estimatedFilingTimeSeconds} /> sorting and filing −{' '}
              <Duration seconds={bulkUpload.estimatedScanTimeSeconds} /> scanning ={' '}
              <Duration seconds={bulkUpload.estimatedTotalTimeSavedSeconds} /> saved.
            </p>
          )}
          <p className="text-sm text-white/85 mt-2">
            This combines estimates for both Email and Bulk Document processing.
            We estimate that it takes about 20s to scan a document and email it, or 40s to send it to our Bulk Document system.
            Whilst we estimate manual sorting and filing time to be at least 60s per repair order and 45s for all the other document types.
            This is difference is mostly because there's often a lot of Repair Orders and also more organisational effort put into to being able to find them again.
            We obviously subtract the time assumed for scanning from the estimated manual filing time. Bulk Documents are designed to split into multiple records, which is why they can reduce total filing time significantly as you don't need to sort and file each one individually.
          </p>
        </section>
      )}

      <section>
        <h3 className="text-lg font-semibold text-white mb-2">Search time</h3>
        {luceneSearchStats != null && luceneSearchStats.count > 0 && (
          <p className="text-sm mt-2 text-white/85">
            <Duration seconds={Math.floor(luceneSearchStats.processingTimeTotal)} /> total
          </p>
        )}
        <p className="text-sm text-white/85 mt-2">
          The average Universal search response time is shown as a separate stat and shows what it's been like in the last 3 months.
          But we also know the total amount of time spent doing that API call searching for documents in total. So as mentioned, we remove that from the Document Retrieval time saved.
        </p>

      </section>
    </div>
  )
}
